import React, { useState } from 'react';
import { Modal, Typography, Button, Space, Input, Select, Checkbox, message, Tag, Divider } from 'antd';
import {
    CloseOutlined,
    PlusOutlined,
    AppstoreOutlined,
    BlockOutlined,
    BuildOutlined,
    LayoutOutlined,
    MobileOutlined,
    TabletOutlined,
    DesktopOutlined,
    DeleteOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;

/**
 * ComponentCreator - Modal for creating new atoms, molecules, organisms, or templates
 */
const ComponentCreator = ({
    visible,
    layer, // 'atoms', 'molecules', 'organisms', 'templates'
    canvasId,
    designKit,
    onClose,
    onCreate
}) => {
    const [loading, setLoading] = useState(false);
    const [component, setComponent] = useState({
        name: '',
        type: '',
        devices: ['mobile', 'tablet', 'desktop'],
        variants: [],
        states: ['default'],
        defaultWidth: 320,
        defaultHeight: 48,
        props: {}
    });
    const [newVariant, setNewVariant] = useState('');
    const [newState, setNewState] = useState('');

    // Reset form when layer changes
    React.useEffect(() => {
        if (visible) {
            setComponent({
                name: '',
                type: '',
                devices: ['mobile', 'tablet', 'desktop'],
                variants: [],
                states: ['default'],
                defaultWidth: layer === 'atoms' ? 120 : layer === 'molecules' ? 280 : 320,
                defaultHeight: layer === 'atoms' ? 40 : layer === 'molecules' ? 64 : layer === 'templates' ? 812 : 200,
                props: {}
            });
        }
    }, [visible, layer]);

    const layerConfig = {
        atoms: {
            icon: <AppstoreOutlined />,
            color: '#ffa657',
            label: 'Atom',
            description: 'Basic building blocks like buttons, inputs, icons'
        },
        molecules: {
            icon: <BlockOutlined />,
            color: '#7ee787',
            label: 'Molecule',
            description: 'Combinations of atoms like search bars, form fields'
        },
        organisms: {
            icon: <BuildOutlined />,
            color: '#a371f7',
            label: 'Organism',
            description: 'Complex components like nav bars, cards, lists'
        },
        templates: {
            icon: <LayoutOutlined />,
            color: '#58a6ff',
            label: 'Template',
            description: 'Full page layouts combining organisms'
        }
    };

    const config = layerConfig[layer] || layerConfig.atoms;

    const handleCreate = async () => {
        if (!component.name?.trim()) {
            message.error('Component name is required');
            return;
        }
        if (!component.type?.trim()) {
            message.error('Component type is required');
            return;
        }

        setLoading(true);
        try {
            await onCreate?.(layer, component);
            message.success(`${config.label} created successfully`);
            onClose();
        } catch (error) {
            message.error(error.message || 'Failed to create component');
        } finally {
            setLoading(false);
        }
    };

    const addVariant = () => {
        if (newVariant.trim() && !component.variants.includes(newVariant.trim())) {
            setComponent(prev => ({
                ...prev,
                variants: [...prev.variants, newVariant.trim()]
            }));
            setNewVariant('');
        }
    };

    const removeVariant = (variant) => {
        setComponent(prev => ({
            ...prev,
            variants: prev.variants.filter(v => v !== variant)
        }));
    };

    const addState = () => {
        if (newState.trim() && !component.states.includes(newState.trim())) {
            setComponent(prev => ({
                ...prev,
                states: [...prev.states, newState.trim()]
            }));
            setNewState('');
        }
    };

    const removeState = (state) => {
        if (state === 'default') return; // Can't remove default state
        setComponent(prev => ({
            ...prev,
            states: prev.states.filter(s => s !== state)
        }));
    };

    const deviceOptions = [
        { value: 'mobile', label: 'Mobile', icon: <MobileOutlined /> },
        { value: 'tablet', label: 'Tablet', icon: <TabletOutlined /> },
        { value: 'desktop', label: 'Desktop', icon: <DesktopOutlined /> }
    ];

    const predefinedStates = ['default', 'hover', 'active', 'disabled', 'loading', 'error', 'success'];

    // Only show states for molecules and above
    const showStates = layer !== 'atoms';

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            closable={false}
            width={520}
            styles={{
                body: { padding: 0, background: '#0d1117' },
                content: { background: '#0d1117', borderRadius: 12 }
            }}
        >
            {/* Header */}
            <div style={{
                padding: '16px 20px',
                borderBottom: '1px solid #30363d',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ color: config.color }}>{config.icon}</span>
                    <Title level={5} style={{ margin: 0, color: '#e6edf3' }}>
                        New {config.label}
                    </Title>
                </div>
                <Button
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={onClose}
                    style={{ color: '#8b949e' }}
                />
            </div>

            {/* Content */}
            <div style={{ padding: 20 }}>
                {/* Description */}
                <div style={{
                    padding: 12,
                    background: `${config.color}15`,
                    borderRadius: 8,
                    marginBottom: 20
                }}>
                    <Text style={{ color: '#8b949e', fontSize: 12 }}>
                        {config.description}
                    </Text>
                </div>

                {/* Name and Type */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                    <div style={{ flex: 1 }}>
                        <Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 6 }}>
                            NAME
                        </Text>
                        <Input
                            value={component.name}
                            onChange={(e) => setComponent(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="e.g., Primary Button"
                            style={{
                                background: '#161b22',
                                border: '1px solid #30363d',
                                borderRadius: 6
                            }}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 6 }}>
                            TYPE (camelCase)
                        </Text>
                        <Input
                            value={component.type}
                            onChange={(e) => setComponent(prev => ({ ...prev, type: e.target.value }))}
                            placeholder="e.g., primaryButton"
                            style={{
                                background: '#161b22',
                                border: '1px solid #30363d',
                                borderRadius: 6
                            }}
                        />
                    </div>
                </div>

                {/* Device Compatibility */}
                <div style={{ marginBottom: 16 }}>
                    <Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 6 }}>
                        DEVICE COMPATIBILITY
                    </Text>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {deviceOptions.map(device => (
                            <div
                                key={device.value}
                                onClick={() => {
                                    setComponent(prev => ({
                                        ...prev,
                                        devices: prev.devices.includes(device.value)
                                            ? prev.devices.filter(d => d !== device.value)
                                            : [...prev.devices, device.value]
                                    }));
                                }}
                                style={{
                                    flex: 1,
                                    padding: '8px 12px',
                                    background: component.devices.includes(device.value) ? '#58a6ff20' : '#161b22',
                                    border: `1px solid ${component.devices.includes(device.value) ? '#58a6ff' : '#30363d'}`,
                                    borderRadius: 6,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 6,
                                    transition: 'all 0.2s'
                                }}
                            >
                                <span style={{ color: component.devices.includes(device.value) ? '#58a6ff' : '#8b949e' }}>
                                    {device.icon}
                                </span>
                                <Text style={{ color: component.devices.includes(device.value) ? '#e6edf3' : '#8b949e', fontSize: 12 }}>
                                    {device.label}
                                </Text>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Default Dimensions */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                    <div style={{ flex: 1 }}>
                        <Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 6 }}>
                            DEFAULT WIDTH (px)
                        </Text>
                        <Input
                            type="number"
                            value={component.defaultWidth}
                            onChange={(e) => setComponent(prev => ({ ...prev, defaultWidth: parseInt(e.target.value) || 0 }))}
                            style={{
                                background: '#161b22',
                                border: '1px solid #30363d',
                                borderRadius: 6
                            }}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 6 }}>
                            DEFAULT HEIGHT (px)
                        </Text>
                        <Input
                            type="number"
                            value={component.defaultHeight}
                            onChange={(e) => setComponent(prev => ({ ...prev, defaultHeight: parseInt(e.target.value) || 0 }))}
                            style={{
                                background: '#161b22',
                                border: '1px solid #30363d',
                                borderRadius: 6
                            }}
                        />
                    </div>
                </div>

                <Divider style={{ margin: '16px 0', borderColor: '#30363d' }} />

                {/* Variants */}
                <div style={{ marginBottom: 16 }}>
                    <Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 6 }}>
                        VARIANTS
                    </Text>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                        {component.variants.map(variant => (
                            <Tag
                                key={variant}
                                closable
                                onClose={() => removeVariant(variant)}
                                style={{
                                    background: '#21262d',
                                    border: '1px solid #30363d',
                                    color: '#e6edf3',
                                    borderRadius: 4
                                }}
                            >
                                {variant}
                            </Tag>
                        ))}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Input
                            value={newVariant}
                            onChange={(e) => setNewVariant(e.target.value)}
                            onPressEnter={addVariant}
                            placeholder="Add variant..."
                            size="small"
                            style={{
                                background: '#161b22',
                                border: '1px solid #30363d',
                                borderRadius: 6
                            }}
                        />
                        <Button
                            type="text"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={addVariant}
                            style={{ color: '#58a6ff' }}
                        />
                    </div>
                </div>

                {/* States (for molecules and up) */}
                {showStates && (
                    <div>
                        <Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 6 }}>
                            STATES
                        </Text>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                            {component.states.map(state => (
                                <Tag
                                    key={state}
                                    closable={state !== 'default'}
                                    onClose={() => removeState(state)}
                                    style={{
                                        background: state === 'default' ? '#58a6ff20' : '#21262d',
                                        border: `1px solid ${state === 'default' ? '#58a6ff' : '#30363d'}`,
                                        color: state === 'default' ? '#58a6ff' : '#e6edf3',
                                        borderRadius: 4
                                    }}
                                >
                                    {state}
                                </Tag>
                            ))}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Select
                                value={newState || undefined}
                                onChange={setNewState}
                                placeholder="Add state..."
                                size="small"
                                style={{ flex: 1 }}
                                options={predefinedStates
                                    .filter(s => !component.states.includes(s))
                                    .map(s => ({ value: s, label: s }))}
                            />
                            <Button
                                type="text"
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={addState}
                                style={{ color: '#58a6ff' }}
                                disabled={!newState}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div style={{
                padding: '16px 20px',
                borderTop: '1px solid #30363d',
                display: 'flex',
                gap: 12,
                justifyContent: 'flex-end'
            }}>
                <Button onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                    loading={loading}
                >
                    Create {config.label}
                </Button>
            </div>
        </Modal>
    );
};

export default ComponentCreator;
